import React from "react";
import { defaultRangeExtractor, useVirtualizer } from "../react-virtual";
import { observeContainer } from "../utils";
import { useListCursor } from "./useListCursor";
import classNames from "classnames";
import { DRAG_OVERLAY } from "../constants";
export const LOOK_AHEAD_COUNT = 15;
export const useList = (props, options) => {
    const { itemHeight, headerItemHeight, overscanRowCount, isDynamic, footerItemHeight, isStickyFooterItem, itemWidth = "100%", onResize, disableVirtual = false, hasCursor = false, onSelectedIndexChanged, getItemKey } = props;
    const { hasMoreRows = false } = props.infiniteLoad ? props : {};
    const headerItemCount = props.headerItemCount ?? 0;
    const footerItemCount = hasMoreRows && !isStickyFooterItem ? 0 : props.footerItemCount ?? 0;
    const itemCount = (hasMoreRows ? props.itemCount + LOOK_AHEAD_COUNT : props.itemCount) + headerItemCount + footerItemCount;
    // const itemCount = hasMoreRows ? itemCount2 + LOOK_AHEAD_COUNT : itemCount2;
    // const headerItemCount = Math.min(props.headerItemCount || 0, itemCount);
    // const footerItemCount = hasMoreRows && !isStickyFooterItem ? 0 : Math.min(props.footerItemCount || 0, itemCount - headerItemCount);
    const { pageHeaderHeight, pageFooterHeight, isStickyPageHeader, isStickyPageFooter, activeRowIndex, containerElement } = options;
    const itemHeightFn = React.useMemo(() => {
        const isItemHeightFunction = typeof itemHeight === "function";
        if (!isItemHeightFunction) {
            return (index) => {
                if (index < headerItemCount)
                    return headerItemHeight ?? itemHeight;
                else if (footerItemCount > 0 && index >= itemCount - footerItemCount)
                    return footerItemHeight ?? itemHeight;
                else
                    return itemHeight;
            };
        }
        else {
            if ((headerItemCount === 0 || !headerItemHeight) && (footerItemCount === 0 || !footerItemHeight))
                return itemHeight;
            return (index) => {
                if (index < headerItemCount)
                    return headerItemHeight ?? itemHeight(index);
                else if (footerItemCount > 0 && index >= itemCount - footerItemCount)
                    return footerItemHeight ?? itemHeight(index);
                else
                    return itemHeight(index);
            };
        }
    }, [itemHeight, headerItemCount, headerItemHeight, footerItemCount, itemCount, footerItemHeight]);
    const footerIndices = React.useMemo(() => {
        return footerItemCount > 0 ? new Array(footerItemCount)
            .fill(itemCount - footerItemCount).map((v, index) => v + index) : [];
    }, [footerItemCount, itemCount]);
    const rowRangeExtractor = React.useCallback((range) => {
        if (range.startIndex === range.endIndex && itemCount > 1)
            return [];
        if (disableVirtual) {
            return [...Array(itemCount)].map((x, index) => index);
        }
        const defaultRange = defaultRangeExtractor(range);
        if (defaultRange.length > 0 && activeRowIndex && !defaultRange.includes(activeRowIndex)) {
            const { 0: first, length, [length - 1]: last } = defaultRange;
            if (activeRowIndex < first)
                defaultRange.unshift(activeRowIndex);
            else if (activeRowIndex > last)
                defaultRange.push(activeRowIndex);
        }
        for (let i = headerItemCount - 1; i >= 0; i--) {
            if (!defaultRange.includes(i))
                defaultRange.unshift(i);
        }
        footerIndices.forEach(footerIndex => {
            if (!defaultRange.includes(footerIndex))
                defaultRange.push(footerIndex);
        });
        return defaultRange;
    }, [activeRowIndex, disableVirtual, headerItemCount, footerIndices, itemCount]);
    const listHeaderHeight = React.useMemo(() => {
        return new Array(headerItemCount).fill(0).reduce((p, c, index) => p + itemHeightFn(index), 0);
    }, [headerItemCount, itemHeightFn]);
    const listFooterHeight = React.useMemo(() => {
        return footerItemCount > 0 && isStickyFooterItem ? footerIndices.reduce((p, index) => p + itemHeightFn(index), 0) : 0;
    }, [footerIndices, footerItemCount, isStickyFooterItem, itemHeightFn]);
    const getItemKeyFn = React.useMemo(() => {
        if (!getItemKey)
            return undefined;
        return (index) => {
            if (index < headerItemCount)
                return `header~${index}`;
            else if (index >= itemCount - footerItemCount)
                return `footer~${index}`;
            else
                return getItemKey(index - headerItemCount);
        };
    }, [headerItemCount, footerItemCount, getItemKey, itemCount]);
    const rowVirtualizer = useVirtualizer({
        count: itemCount,
        getScrollElement: () => containerElement,
        estimateSize: itemHeightFn,
        rangeExtractor: rowRangeExtractor,
        overscan: overscanRowCount || 3,
        paddingStart: pageHeaderHeight,
        paddingEnd: pageFooterHeight,
        scrollPaddingStart: (isStickyPageHeader ? pageHeaderHeight : 0) + listHeaderHeight,
        scrollPaddingEnd: (isStickyPageFooter ? pageFooterHeight : 0) + listFooterHeight,
        getItemKey: getItemKeyFn,
        indexAttribute: "data-item-index",
    });
    React.useLayoutEffect(() => {
        return observeContainer(containerElement, onResize);
    }, [onResize, containerElement]);
    const scrollToIndex = React.useCallback((index, options) => {
        options = isDynamic ? { ...options, behavior: "auto" } : options;
        rowVirtualizer.scrollToIndex(index, options);
    }, [isDynamic, rowVirtualizer]);
    const scrollToOffset = React.useCallback((toOffset, options) => {
        options = isDynamic ? { ...options, behavior: "auto" } : options;
        rowVirtualizer.scrollToOffset(toOffset, options);
    }, [isDynamic, rowVirtualizer]);
    const [cursor, setCursor] = useListCursor({
        containerElement: containerElement,
        hasCursor: hasCursor,
        itemCount: itemCount,
        headerItemCount: headerItemCount,
        footerItemCount: footerItemCount,
        onSelectedIndexChanged: onSelectedIndexChanged,
        scrollToCursor: scrollToIndex,
        pageSize: rowVirtualizer.range.endIndex - rowVirtualizer.range.startIndex
    });
    const rows = rowVirtualizer.getVirtualItems();
    const headerRows = rows.slice(0, headerItemCount);
    const bodyRows = rows.slice(headerItemCount, footerItemCount > 0 ? footerItemCount * -1 : undefined);
    const footerRows = footerItemCount > 0 ? rows.slice(footerItemCount * -1) : [];
    const buildItemProps = (virtualRow, renderType) => {
        const minHeight = typeof itemHeight === "function" ? itemHeight(virtualRow.index) : itemHeight;
        const isContent = renderType !== "header";
        const position = renderType === "overlay" ? "static" : "absolute";
        const className = renderType === "overlay" ? DRAG_OVERLAY : cursor === virtualRow.index ? "x-cursor" : undefined;
        const fixedRowOffset = footerRows[0]?.start ?? 0;
        // const rows = rowVirtualizer.getVirtualItems();
        // const x = footerIndices.length > 0 ? rows.find(a => a.index === footerIndices[0])?.start
        return {
            rowId: virtualRow.key,
            rowIndex: virtualRow.index,
            dataRowIndex: virtualRow.index - headerItemCount,
            style: {
                position: position,
                top: renderType === "footer" ? (virtualRow.start - fixedRowOffset) : virtualRow.start - (isContent ? 0 : pageHeaderHeight),
                left: 0,
                width: itemWidth,
                height: isDynamic && isContent ? "auto" : virtualRow.size,
                minHeight: isDynamic && isContent ? minHeight : undefined,
            },
            className: renderType === "header" ? classNames(className, "x-list-header") : classNames(className, "x-list-item"),
            isScrolling: rowVirtualizer.isScrolling,
            measureElement: isDynamic && renderType === "content" ? rowVirtualizer.measureElement : undefined,
            hasCursor: cursor === virtualRow.index,
            attributes: { "data-item-index": virtualRow.index, "data-scrolling": rowVirtualizer.isScrolling }
        };
    };
    const listRef = React.useMemo(() => ({
        setCursor: setCursor,
        scrollToOffset: scrollToOffset,
        scrollToIndex: scrollToIndex,
    }), [scrollToIndex, scrollToOffset, setCursor]);
    return {
        rowVirtualizer,
        buildItemProps,
        listRef,
        headerRows,
        bodyRows,
        footerRows
    };
};
